import { useState, useEffect } from "react";

// Third Party
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

// UI
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { FileUpload } from "@/components/FileUpload";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

// Icons
import { Pen, Save, Trash2, CirclePlus } from "lucide-react";

// Services and Type
import type {
	MutateDepartmentType,
	DepartmentDetail as DepartmentDetailType,
} from "@/types/soe";
import {
	createCompanyDepartment,
	updateCompanyDepartment,
} from "@/services/soe";

interface DepartmentDetail {
	deptName: string;
	headCount: number | null;
	femaleHeadCount: number | null;
	empCount: number | null;
	femaleEmpCount: number | null;
}

export const Route = createFileRoute(
	"/_layout-private/account-management/soe/_information/department/mutate",
)({
	component: MutateDepartment,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			id: search.id as string | undefined,
			data: search.data as string | undefined,
		};
	},
});

function MutateDepartment() {
	const rawId = localStorage.getItem("companyId") ?? "";
	const id = rawId.replace(/^"(.+)"$/, "$1") ?? "";
	const navigate = useNavigate();
	const { id: departmentId, data } = Route.useSearch();
	const { toast } = useToast();
	const queryClient = useQueryClient();

	// States
	const [animationParent] = useAutoAnimate();
	const [parseData, setParseData] = useState({} as MutateDepartmentType);
	const [file, setFile] = useState("");
	const [selectedYear, setSelectedYear] = useState<string>("");
	const [companyDeptDetails, setCompanyDeptDetails] = useState<
		DepartmentDetail[]
	>([
		{
			deptName: "",
			headCount: null,
			femaleHeadCount: null,
			empCount: null,
			femaleEmpCount: null,
		},
	]);

	// Generate years from 1975 to current year
	const currentYear = new Date().getFullYear();
	const years = Array.from({ length: currentYear - 1975 + 1 }, (_, i) => currentYear - i);

	// Effects
	useEffect(() => {
		if (data) {
			const parsed = JSON.parse(data) as DepartmentDetailType;

			setParseData(parsed);
			setCompanyDeptDetails(parsed.companyDeptDetails);
			setFile(parsed.docFile);
			setSelectedYear(parsed.yearly?.toString() || "");
		}
	}, [data]);

	// Mutation
	const createDepartmentMutation = useMutation({
		mutationFn: (data: MutateDepartmentType) => createCompanyDepartment(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["departments", id] });
			toast({
				title: "ສຳເລັດ",
				description: "ສ້າງຂ້ໍມູນພະແນກສຳເລັດ",
			});
			navigate({
				to: "..",
				replace: true,
			});
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	const updateDepartmentMutation = useMutation({
		mutationFn: (data: MutateDepartmentType) =>
			updateCompanyDepartment(departmentId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["departments", id] });
			toast({
				title: "ສຳເລັດ",
				description: "ແກ້ໄຂຂ້ໍມູນພະແນກສຳເລັດ",
			});
			navigate({
				to: "..",
				replace: true,
			});
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	// Functions
	const handleAddDepartment = () => {
		setCompanyDeptDetails([
			...companyDeptDetails,
			{
				deptName: "",
				headCount: null,
				femaleHeadCount: null,
				empCount: null,
				femaleEmpCount: null,
			},
		]);
	};

	const handleRemoveDepartment = (index: number) => {
		if (companyDeptDetails.length === 1) {
			toast({
				title: "ແຈ້ງເຕືອນ",
				description: "ຕ້ອງມີຢ່າງໜ້ອຍ 1 ພະແນກ",
				variant: "destructive",
			});
			return;
		}

		const newDepts = [...companyDeptDetails];
		newDepts.splice(index, 1);
		setCompanyDeptDetails(newDepts);
	};

	const handleInputChange = (
		index: number,
		field: keyof DepartmentDetail,
		value: string | number,
	) => {
		const newDepts = [...companyDeptDetails];

		if (field !== "deptName") {
			// Only allow numbers and empty string
			if (value === "" || /^\d*$/.test(value as string)) {
				newDepts[index][field] = value === "" ? null : Number(value);
				setCompanyDeptDetails(newDepts);
			}
		} else {
			newDepts[index][field] = value as string;
			setCompanyDeptDetails(newDepts);
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		// setIsSubmitting(true);

		if (!selectedYear && !parseData?.yearly) {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ກະລຸນາເລືອກປີ",
				variant: "destructive",
			});
			return;
		}

		if (!file) {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ກະລຸນາອັບໂຫຼດເອກະສານ",
				variant: "destructive",
			});

			return;
		}

		const formData = new FormData(e.target as HTMLFormElement);

		// Validate form
		const isValid = companyDeptDetails.every(
			(dept) =>
				dept.deptName.trim() !== "" &&
				dept.headCount !== null &&
				dept.femaleHeadCount !== null &&
				dept.empCount !== null &&
				dept.femaleEmpCount !== null,
		);

		if (!isValid) {
			toast({
				title: "ຂໍ້ມູນບໍ່ຄົບຖ້ວນ",
				description: "ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ",
				variant: "destructive",
			});
			// setIsSubmitting(false);
			return;
		}

		const payload = {
			companyId: id,
			docFile: file,
			yearly: parseData?.yearly || Number(selectedYear),
			companyDeptDetails: companyDeptDetails,
		};

		// If editing, update only the first department
		if (departmentId) {
			updateDepartmentMutation.mutate(payload);
		} else {
			// If creating, create all departments

			createDepartmentMutation.mutate(payload);
		}
	};

	// Data
	const mutationPending =
		createDepartmentMutation.isPending || updateDepartmentMutation.isPending;

	return (
		<div className="space-y-[24px]">
			<div className="text-[18px]">
				<h2 className="font-semibold text-zinc-950">ຂ້ໍມູນການຈັດຕັ້ງພະແນກ</h2>
				<p className="text-zinc-500">
					ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນການຈັດຕັ້ງພະແນກຄະນະອຳນວຍການຂອງລັດວິສາຫະກິດ
				</p>

				<Separator className="my-6" />
			</div>

			<form onSubmit={handleSubmit} className="space-y-[24px]">
				<div>
					<Label>
						ປີ <span className="text-red-500">*</span>
					</Label>
					<Select
						value={selectedYear || parseData?.yearly?.toString()}
						onValueChange={setSelectedYear}
						disabled={parseData?.yearly ? true : false}
					>
						<SelectTrigger>
							<SelectValue placeholder="ເລືອກປີ..." />
						</SelectTrigger>
						<SelectContent>
							{years.map((year) => (
								<SelectItem key={year} value={year.toString()}>
									{year}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div ref={animationParent} className="space-y-6">
					{companyDeptDetails.map((dept, index) => (
						<div key={index} className="p-4 space-y-4 rounded-lg border">
							<div className="flex justify-between items-center">
								<h3 className="font-medium">ພະແນກທີ {index + 1}</h3>
								{index ? (
									<Button
										type="button"
										variant="ghost"
										size="icon"
										onClick={() => handleRemoveDepartment(index)}
									>
										<Trash2 className="w-5 h-5 text-red-500" />
									</Button>
								) : null}
							</div>

							<div>
								<Label htmlFor={`deptName-${index}`}>
									ຊື່ພະແນກການ/ຝ່າຍ <span className="text-red-500">*</span>
								</Label>
								<Input
									id={`deptName-${index}`}
									required
									value={dept.deptName}
									placeholder="ປ້ອນຊື່ພະແນກການ/ຝ່າຍ..."
									onChange={(e) =>
										handleInputChange(index, "deptName", e.target.value)
									}
								/>
							</div>

							<div className="grid grid-cols-2 gap-4">
								<div>
									<Label htmlFor={`headCount-${index}`}>
										ຄະນະພະແນກ <span className="text-red-500">*</span>
									</Label>
									{/* <Input
										id={`headCount-${index}`}
										required
										type="number"
										value={dept.headCount === null ? '' : dept.headCount}
										onChange={(e) => handleInputChange(index, 'headCount', e.target.value)}
										placeholder="ຄະນະພະແນກ..."
									/> */}
									<Input
										id={`headCount-${index}`}
										required
										type="text"
										inputMode="numeric"
										pattern="\d*"
										value={dept.headCount === null ? "" : dept.headCount}
										onChange={(e) =>
											handleInputChange(index, "headCount", e.target.value)
										}
										placeholder="ຄະນະພະແນກ..."
									/>
								</div>

								<div>
									<Label htmlFor={`femaleHeadCount-${index}`}>
										ຄະນະພະແນກ (ຍິງ) <span className="text-red-500">*</span>
									</Label>
									<Input
										required
										type="text"
										inputMode="numeric"
										pattern="\d*"
										value={
											dept.femaleHeadCount === null ? "" : dept.femaleHeadCount
										}
										onChange={(e) =>
											handleInputChange(
												index,
												"femaleHeadCount",
												e.target.value,
											)
										}
										placeholder="ຄະນະພະແນກ (ຍິງ)..."
									/>
								</div>

								<div>
									<Label htmlFor={`empCount-${index}`}>
										ຈຳນວນພະນັກງານ (ທັງໝົດ) <span className="text-red-500">*</span>
									</Label>
									<Input
										id={`empCount-${index}`}
										type="text"
										inputMode="numeric"
										pattern="\d*"
										value={dept.empCount === null ? "" : dept.empCount}
										onChange={(e) =>
											handleInputChange(index, "empCount", e.target.value)
										}
										placeholder="ຈຳນວນພະນັກງານ (ທັງໝົດ)..."
									/>
								</div>

								<div>
									<Label htmlFor={`femaleEmpCount-${index}`}>
										ຈຳນວນພະນັກງານ (ຍິງ) <span className="text-red-500">*</span>
									</Label>
									<Input
										id={`femaleEmpCount-${index}`}
										type="text"
										inputMode="numeric"
										pattern="\d*"
										value={
											dept.femaleEmpCount === null ? "" : dept.femaleEmpCount
										}
										onChange={(e) =>
											handleInputChange(index, "femaleEmpCount", e.target.value)
										}
										placeholder="ຈຳນວນພະນັກງານ (ຍິງ)..."
									/>
								</div>
							</div>
						</div>
					))}
				</div>

				<Button
					type="button"
					onClick={handleAddDepartment}
					variant={"secondary"}
					className="flex gap-3 justify-center items-center"
				>
					<CirclePlus />
					ເພີ່ມພະແນກ
				</Button>

				<div className="flex gap-x-1 items-center mt-4">
					<FileUpload
						required
						currentFile={file}
						onFileUpload={(value) => setFile(value)}
					/>
				</div>

				<div className="flex gap-4 justify-end items-center w-full">
					<Button
						type="button"
						variant="outline"
						onClick={() => navigate({ to: "..", replace: true })}
					>
						ຍົກເລີກ
					</Button>
					<Button disabled={mutationPending} type="submit">
						{mutationPending ? (
							<Spinner className="text-white" />
						) : departmentId ? (
							<>
								<Pen className="mr-2" /> ແກ້ໄຂ
							</>
						) : (
							<>
								<Save className="mr-2" /> ບັນທຶກ
							</>
						)}
					</Button>
				</div>
			</form>
		</div>
	);
}
